import { UseQueryOptions, useMutation, useQuery } from 'react-query';

import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

import {
  deleteReadStories,
  fetchMyLatestStory,
  fetchMyStory,
  fetchStory,
  fetchStoryIknow,
  getPublishChallengeInfo,
  getReadEvent,
  getWeeklySumamries,
} from '@api/story';
import { QUERY_KEYS } from '@constants';
import { IReadEvent, IStoryDetail, IStoryMap } from '@model';

export const useStoryQuery = () => {
  const useFetchStory = (
    storyId: number,
    options?: UseQueryOptions<IStoryDetail>
  ) => {
    const queryKey = QUERY_KEYS.STORY.ONE(storyId);
    const query = useQuery<IStoryDetail>(queryKey, () => fetchStory(storyId), {
      staleTime: minutesToMilliseconds(15),
      cacheTime: minutesToMilliseconds(15),
      ...options,
    });
    return query;
  };

  const useFetchMyStory = (storyId: number) => {
    const query = useQuery(QUERY_KEYS.MY_STORY.STORY(storyId), () =>
      fetchMyStory(storyId)
    );

    return query;
  };

  const useFetchLatestStory = () => {
    return useQuery('myLatestStory', () => fetchMyLatestStory(), {
      suspense: false,
    });
  };

  const { mutate: removeReadStories } = useMutation((storyIds: number[]) =>
    deleteReadStories(storyIds)
  );

  const useFetchStoryIknow = (
    storyId: number,
    options?: UseQueryOptions<IStoryMap>
  ) => {
    const queryKey = QUERY_KEYS.STORY.ONE(storyId);
    const query = useQuery<IStoryMap>(
      queryKey,
      () => fetchStoryIknow(storyId),
      {
        staleTime: minutesToMilliseconds(15),
        cacheTime: minutesToMilliseconds(15),
        ...options,
      }
    );
    return query;
  };

  const useReadEvent = (code: string) => {
    const queryKey = QUERY_KEYS.STORY.READ_EVENT(code);
    const query = useQuery<IReadEvent>(queryKey, () => getReadEvent(code), {
      staleTime: minutesToMilliseconds(15),
      cacheTime: minutesToMilliseconds(15),
    });
    return query;
  };

  const usePublishChallengeInfo = (storyId: number) => {
    const queryKey = QUERY_KEYS.MY_STORY.PUBLISH_CHALLENGE_INFO(storyId);
    const query = useQuery(queryKey, () => getPublishChallengeInfo(storyId));
    return query;
  };

  const useWeeklySummary = (storyId: number) => {
    const queryKey = QUERY_KEYS.STORY.SUMMARIES(storyId);
    const query = useQuery(queryKey, () => getWeeklySumamries(storyId));
    return query;
  };

  return {
    useFetchStory,
    useFetchMyStory,
    useFetchLatestStory,
    removeReadStories,
    useFetchStoryIknow,
    useReadEvent,
    usePublishChallengeInfo,
    useWeeklySummary,
  };
};
